import { GridCustomCellProps } from '@progress/kendo-react-grid';
import * as React from 'react';
import { ILink } from '../../types/link';
import TripSummary from './TripSummary';
import { Trip } from '.';
import OrderStatusNotePopup from './OrderStatusNotePopup';
import DispatchConfirmationDialog from './DispatchConfirmationDialog';

const StatusCell = (props: GridCustomCellProps) => {
  const [showTripSummary, setShowTripSummary] = React.useState(false);
  const [showOrderStatusNotePopup, setShowOrderStatusNotePopup] = React.useState(false);
  const [showDispatchConfirmation, setShowDispatchConfirmation] = React.useState(false);
  const trip = props.dataItem as Trip;

  // Mirrors DispatchConfirmationStatus in LoadOne.Types. Only the states a dispatcher still
  // owes something on get a label, and that label stands in for the stop status. Once the
  // driver is accounted for the stop status alone says it: confirmed and on the way.
  const dispatchConfirmation = (status: number | null, callAttemptCount: number) => {
    switch (status) {
      case 0:
        // A prior failed attempt with a retry still scheduled also lands here (Pending), and by
        // then CallAttemptCount already counts that finished attempt, so the next one is +1.
        return {
          text: callAttemptCount > 0 ? `Waiting Confirmation (attempt ${callAttemptCount + 1})` : 'Waiting Confirmation',
          color: '#007bff',
        };
      case 3:
        // CallPlaced increments CallAttemptCount before setting this status, so unlike Pending
        // above, the count here already IS the attempt currently ringing, no +1.
        return {
          text: callAttemptCount > 0 ? `Waiting Confirmation (attempt ${callAttemptCount})` : 'Waiting Confirmation',
          color: '#007bff',
        };
      case 4:
      case 5:
      case 8:
        return { text: 'Confirmation Failed', color: 'red' };
      case 7:
        return { text: 'Callback Requested', color: 'red' };
      default:
        return null;
    }
  }

  const orderStopStatusName = (orderStatusName: string, status: number, event: number) => {
    if (orderStatusName) return orderStatusName;

    switch (status) {
      case 100:
        return 'Not Started';
      case 110:
        return `Enroute to ${(event === 1) ? 'PU' : 'DEL'}`;
      case 115:
        return `Arrived at ${(event === 1) ? 'PU' : 'DEL'}`;
      case 120:
        return 'Completed';
      default:
        return 'On Route'; // Deadhead
    }
  }

  // Only stands in for the stop status up to the point the driver arrives (115). Past that the
  // driver has demonstrably got the load, so the real status has to win or Track would hide
  // actual movement behind a confirmation nobody needs any more.
  const confirmation = trip.OrderStopStatus < 115
    ? dispatchConfirmation(trip.DispatchConfirmationStatus, trip.DispatchConfirmationCallAttemptCount)
    : null;

  return <td {...props.tdProps}>
    {showTripSummary && <TripSummary orderId={trip.OrderID} close={() => setShowTripSummary(false)} />}
    {showDispatchConfirmation && <DispatchConfirmationDialog
      OrderID={trip.OrderID!}
      CloseDialog={() => setShowDispatchConfirmation(false)} />}
    {showOrderStatusNotePopup && <OrderStatusNotePopup
      orderId={trip.OrderID}
      statusNote={trip.StatusNote}
      onSave={(statusNote) => {
        trip.StatusNote = statusNote;
        setShowOrderStatusNotePopup(false);
      }}
      onClose={() => setShowOrderStatusNotePopup(false)}
    />}
    {trip.StopSequence} of {trip.StopCount} &nbsp;
    {trip.Links.find((x: ILink) => x.Name === 'TripSummary') && <a
      href="#"
      style={{ color: '#007bff' }}
      onClick={(e) => {
        setShowTripSummary(true);
        e.preventDefault();
      }}
    >TS
    </a>}<br />
    {confirmation ? <a
      href="#"
      style={{ color: confirmation.color }}
      onClick={(e) => {
        setShowDispatchConfirmation(true);
        e.preventDefault();
      }}
    >{confirmation.text}
    </a> : (trip.OrderID ? <a
      href="#"
      style={{ color: '#007bff' }}
      onClick={(e) => {
        setShowOrderStatusNotePopup(true);
        e.preventDefault();
      }}
    >{orderStopStatusName(trip.OrderStatusName, trip.OrderStopStatus, trip.OrderStopEvent)}
    </a> : orderStopStatusName(trip.OrderStatusName, trip.OrderStopStatus, trip.OrderStopEvent))}
    <br />
    <span style={{ cursor: 'pointer' }} onClick={() => setShowOrderStatusNotePopup(true)} className={trip.StatusNote ? "badge badge-info" : "badge badge-light"}>{trip.StatusNote || "Add Note"}</span>
    </td>
  }

  export default React.memo(StatusCell, (prev, next) => prev.dataItem.Hash === next.dataItem.Hash);
